import { AppModule } from 'app/module';
import { JobsInfoBox } from 'module/jobsInfoBox/main';
import { SuggestionList } from 'module/suggestionList/main';
import { AlarmDisruptor } from 'module/alarmDisruptor/main';
import { Memo } from 'module/memo/main';
import { InactiveJobCupcake } from 'module/inactiveJobCupcake/main';
import { mediaQueries } from 'util/mediaQueries';
import { trackGa4 } from 'util/googleAnalytics';
import { KeyfactBox } from 'module/keyfactBox/main';
import { log } from '@karriereat/action-logger';
import { postJson } from 'util/xhr';
import { user } from 'app/user';
import { API_ENDPOINT_LOG_APPLICATION_CLICK } from 'app/endpoints';
import { CONSENT_GROUPS, hasConsent, onConsentChange } from 'util/consent';
import { dangerouslySetInnerHtml } from 'dangerously-set-html-with-scripts';
import Template from './template.hbs';
import './styles.scss';

export class JobContent extends AppModule {
    setTemplate() {
        this.template = Template;
        if (module.hot) {
            module.hot.accept('./template.hbs', () => {
                this.template = Template;
                this.render();
            });
        }
    }

    domBindings() {
        this.dom.iFrame = this.dom.el.querySelector('.m-jobContent__iFrame');
        this.dom.iFrameContainer = this.dom.el.querySelector('.m-jobContent__jobContainer--iframe')
            || this.dom.el.querySelector('.m-jobContent__companyContainer');
        this.dom.jobContainer = this.dom.el.querySelector('.m-jobContent__jobContainer');
        this.dom.jobText = this.dom.el.querySelector('.m-jobContent__jobText');
        this.dom.suggestionList = this.dom.el.querySelector('.m-jobContent__suggestions .m-suggestionList');
        this.dom.lastViewedList = this.dom.el.querySelector('.m-jobContent__lastViewed .m-suggestionList');
        this.dom.jobsInfoBox = this.dom.el.querySelector('.m-jobContent__inactiveBox .m-jobsInfoBox');
        this.dom.alarmDisruptor = this.dom.el.querySelector('.m-jobContent__alarmDisruptor .m-alarmDisruptor');
        this.dom.memoContainer = this.dom.el.querySelector('.m-jobContent__memo');
        this.dom.memo = this.dom.el.querySelector('.m-jobContent__memo .m-memo');
        this.dom.inactiveJobCupcake = this.dom.el.querySelector('.m-jobContent__inactiveJobCupcake');
        this.dom.keyfactBox = this.dom.el.querySelector('.m-jobContent__side .m-keyfactBox__keyfacts');

        /* SuggestionList, jobsInfoBox and alarmDisruptor instance
        is needed in `getPropsFromDom()` which runs before `subs()`. */

        if (this.dom.suggestionList) {
            this.suggestionList = new SuggestionList(this.dom.suggestionList);
        }

        if (this.dom.lastViewedList) {
            this.lastViewedList = new SuggestionList(this.dom.lastViewedList);
        }

        if (this.dom.jobsInfoBox) {
            this.jobsInfoBox = new JobsInfoBox(this.dom.jobsInfoBox);
        }

        if (this.dom.alarmDisruptor) {
            this.alarmDisruptor = new AlarmDisruptor(this.dom.alarmDisruptor);
        }

        if (this.dom.memo) {
            this.memo = new Memo(this.dom.memo);
        }

        if (this.dom.inactiveJobCupcake) {
            this.inactiveJobCupcake = new InactiveJobCupcake(this.dom.inactiveJobCupcake);
        }
    }

    subs() {
        if (this.dom.keyfactBox) {
            this.keyfactBox = new KeyfactBox(this.dom.keyfactBox);
        }
    }

    getPropsFromDom() {
        let suggestionListProps = {};
        let lastViewedListProps = {};
        let jobsInfoBoxProps = {};
        let alarmDisruptorProps = {};
        let inactiveJobCupcakeProps = {};
        let keyfactBoxProps = {};

        if (this.dom.suggestionList) {
            suggestionListProps = this.suggestionList.getPropsFromDom();
        }

        if (this.dom.lastViewedList) {
            lastViewedListProps = this.lastViewedList.getPropsFromDom();
        }

        if (this.dom.jobsInfoBox) {
            jobsInfoBoxProps = this.jobsInfoBox.getPropsFromDom();
        }

        if (this.dom.alarmDisruptor) {
            alarmDisruptorProps = this.alarmDisruptor.getPropsFromDom();
        }

        if (this.dom.inactiveJobCupcake) {
            inactiveJobCupcakeProps = this.inactiveJobCupcake.getPropsFromDom();
        }

        if (this.dom.keyfactBox) {
            keyfactBoxProps = this.keyfactBox.getPropsFromDom();
        }

        return {
            isSeamless: this.dom.el.getAttribute('data-is-seamless') === 'true',
            jobBundleLabel: this.dom.el.getAttribute('data-job-bundle-label'),
            jobContent: {
                id: this.dom.el.getAttribute('data-id'),
                jobBundleId: this.dom.el.getAttribute('data-bundle-id'),
                html: this.dom.el.getAttribute('data-src-doc'),
                text: this.dom.jobText ? this.dom.jobText.innerHTML : '',
                watchlistId: this.dom.el.getAttribute('data-watchlist-id') ?? false,
                isSmartApplyJob: this.dom.el.getAttribute('data-is-smart-apply-job') === 'true',
                inactiveJobCupcake: inactiveJobCupcakeProps,
            },
            jobSuggestions: suggestionListProps,
            isSplitView: (this.dom.el.getAttribute('data-splitview') === 'true'),
            participatingCompanyData: {
                contactPersonName: this.dom.el.getAttribute('data-contactpersonname'),
                roleDescription: this.dom.el.getAttribute('data-roledescription'),
            },
            jobsInfoBox: jobsInfoBoxProps,
            alarmDisruptor: alarmDisruptorProps,
            lastViewed: lastViewedListProps,
            keyfactBox: keyfactBoxProps,
            companySlug: this.dom.el.getAttribute('data-company-slug'),
        };
    }

    ready() {
        this.iFrameInit();

        if (this.isSeamless()) {
            if (this.props.isSplitView && this.dom.jobContainer) {
                // In the split view, html is inserted via innerHtml assignments, which do not execute script nodes.
                // This helper function rebuilds the html fragment and executes all scripts
                dangerouslySetInnerHtml(this.dom.jobContainer, this.props.jobContent.html);
            }
            this.events.emit('jobContentLoaded');

            this.initializeSeamlessJobVideo();
        }

        onConsentChange(() => {
            this.initializeSeamlessJobVideo();
            this.initializeThirdPartyIframeContent();
        });
        if (this.dom.jobText) {
            this.addLinkEvents(this.dom.jobText); // For mobile
        }
    }

    subEvents() {
        this.propagateEvents(this.alarmDisruptor, 'removeModalStyle');
        this.propagateEvents(this.alarmDisruptor, 'addModalStyle');

        this.propagateEvents(this.memo, 'openDeleteModal');
        this.propagateEvents(this.memo, 'showSnackbar');
        this.propagateEvents(this.memo, 'memoSaved');
        this.propagateEvents(this.memo, 'memoDeleted');
        this.propagateEvents(this.memo, 'showMemoOverlay');
        this.propagateEvents(this.memo, 'hideMemoOverlay');
    }

    isSeamless() {
        return Boolean(this.props.isSeamless);
    }

    iFrameInit() {
        const { iFrame } = this.dom;
        let resizeTimeout;

        window.addEventListener('resize', () => {
            if (resizeTimeout) {
                cancelAnimationFrame(resizeTimeout);
            }
            resizeTimeout = requestAnimationFrame(() => {
                this.iFrameUpdate();
            });
        });

        if (!iFrame
            || ((mediaQueries.isMobile() && iFrame.classList.contains('m-jobContent__iFrame--job'))
                && !this.isSeamless())) {
            setTimeout(() => this.events.emit('jobContentLoaded'));
            return;
        }

        setTimeout(() => this.events.emit('jobContentLoading'));

        const iFrameLoaded = this.iFrameLoaded.bind(this);
        iFrame.addEventListener('load', iFrameLoaded);

        let contentDocument = false;
        try {
            contentDocument = (iFrame.contentWindow || iFrame.contentDocument).document;
        } catch {
            // 'Cannot access iframe'
        }

        if (contentDocument) {
            const checkState = () => {
                const location = contentDocument.location ? contentDocument.location.href : 'about:blank';
                if (location !== 'about:blank' && contentDocument.readyState === 'complete') {
                    this.iFrameLoaded();
                } else {
                    setTimeout(checkState, 100);
                }
            };
            checkState();
        }
    }

    iFrameLoaded() {
        this.iFrameUpdate();
        this.initializeThirdPartyIframeContent();
    }

    iFrameUpdate() {
        const { iFrame } = this.dom;
        let contentDocument = false;

        try {
            contentDocument = (iFrame.contentWindow || iFrame.contentDocument).document;
            this.updateIFrameHeight();
            this.addLinkEvents(contentDocument);
        } catch {
            // 'Cannot access iframe'
        }

        iFrame?.classList.remove('m-jobContent__iFrame--loading');
        this.events.emit('jobContentLoaded');
    }

    updateIFrameHeight() {
        let scrollPosition;
        if (this.props.isSplitView) {
            scrollPosition = document.getElementById('job-detail-container').scrollTop;
        } else {
            scrollPosition = window.scrollY;
        }

        const { iFrame } = this.dom;
        iFrame.style.height = 'auto';
        this.dom.iFrameContainer.style.height = 'auto';

        const contentHeight = iFrame?.contentDocument?.documentElement?.scrollHeight;

        if (contentHeight) {
            iFrame.style.height = `${contentHeight}px`;
            this.dom.iFrameContainer.style.height = `${contentHeight}px`;
        }

        // Restore scroll position to avoid scroll jumping
        if (this.props.isSplitView) {
            document.getElementById('job-detail-container').scrollTop = scrollPosition;
        } else {
            window.scroll(0, scrollPosition);
        }
    }

    initializeSeamlessJobVideo() {
        if (!this.isSeamless()) return;

        if (hasConsent(CONSENT_GROUPS.performance)) {
            // SjaTemplate namespace exposed by SJA template renderer
            window.SjaTemplate?.loadVideo();
        }
    }

    // Prevent third-party libraries, such as YouTube videos and Google Maps,
    // from bypassing our cookie consent mechanism.
    initializeThirdPartyIframeContent() {
        if (hasConsent(CONSENT_GROUPS.performance)) {
            this.dom.iFrame?.contentWindow.postMessage({ type: 'consent', consentGranted: true }, '*');
        }
    }

    onSendMailToEvent() {
        trackGa4({
            event: 'click_element',
            element: 'JOBD: External Link',
            element_detail: 'Mailto',
        });
    }

    onSendOtherEvent(event) {
        trackGa4({
            event: 'click_element',
            element: 'JOBD: External Link',
            element_detail: event.target.innerHTML,
        });
    }

    onSendApplyLinkEvent(method) {
        trackGa4({
            event: 'job_apply_click',
            application_type: 'SmartBewerben',
            job_id: this.props.jobContent.id ?? null,
            job_type: this.props.isSeamless ? 'SJA' : 'Standard',
            product_type: this.props.jobBundleLabel,
            method,
            user_data: {
                email: user.getUserMail(),
            },
        });

        const data = {
            jobId: this.props.jobContent.id ?? null,
            jobBundleId: this.props.jobContent.jobBundleId,
            type: 'smartbewerben',
        };

        log('apply-click', data);
        this.logApplicationClick(data);
    }

    logApplicationClick(data) {
        postJson(
            `${API_ENDPOINT_LOG_APPLICATION_CLICK}`,
            { data },
        );
    }

    addLinkEvents(contentDocument) {
        const links = contentDocument.querySelectorAll('a');

        links.forEach((link) => {
            if (link.hasAttribute('data-is-smart-apply-link')
                && link.getAttribute('data-is-smart-apply-link') === 'true') {
                link.addEventListener('click', () => { this.onSendApplyLinkEvent('Inline Apply'); });
            } else if (link.href.startsWith('mailto')) {
                link.addEventListener('click', this.onSendMailToEvent);
            } else {
                link.addEventListener('click', this.onSendOtherEvent);
            }
        });
    }

    allowPointerEvents() {
        if (this.dom.iFrame) {
            this.dom.iFrame.classList.remove('m-jobContent__iFrame--noPointerEvents');
        }
    }

    deleteMemo() {
        if (this.memo) {
            this.memo.deleteMemo();
        }
    }

    saveMemo() {
        if (this.memo) {
            this.memo.saveMemo();
        }
    }

    memoContentSimilar() {
        if (this.memo) {
            return this.memo.memoContentSimilar();
        }
        return false;
    }

    getTextareaValue() {
        if (this.memo) {
            return this.memo.getTextareaValue();
        }
        return false;
    }
}
